import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import PiecesConsommees from "../components/PiecesConsommees";
import Layout from "../components/Layout";
import ModaleConfirmation from "../components/ModaleConfirmation";
import { useModules } from "../modules";

export const TYPES_OPERATION = [
  // Le carburant en tete : c'est le poste le plus frequent et le plus lourd
  // d'une flotte, donc celui qu'on saisit le plus souvent.
  { valeur: "carburant", label: "Carburant (plein)" },
  { valeur: "vidange", label: "Vidange" },
  { valeur: "plaquettes", label: "Plaquettes" },
  { valeur: "disques", label: "Disques" },
  { valeur: "pneus", label: "Pneus" },
  { valeur: "lavage", label: "Lavage" },
  { valeur: "changement_main", label: "Changement de main" },
  { valeur: "autre", label: "Autre" },
];

export function labelTypeOperation(valeur) {
  const t = TYPES_OPERATION.find((x) => x.valeur === valeur);
  return t ? t.label : valeur;
}

/** Le libelle d'UNE operation : « Autre » seul ne dit rien dans un historique.
 *
 * Pour ce type, la precision saisie (obligatoire depuis H5) remplace le mot
 * « Autre » -- meme regle que les references du vehicule. La repartition PAR
 * TYPE, elle, garde `labelTypeOperation` : c'est un total, pas une ligne. */
export function libelleOperation(op) {
  if (op.type_operation === "autre" && op.libelle_libre) {
    return op.libelle_libre;
  }
  return labelTypeOperation(op.type_operation);
}

/** Un kilometrage qui contredit l'historique du camion (audit B3, 28/09/2026).
 *
 * Il etait accepte sans un mot. Une faute de frappe sur le compteur fausse le
 * cout au kilometre et la consommation -- deux chiffres qui servent a decider
 * de garder ou de vendre un camion --, et rien ne la faisait remarquer.
 *
 * ON COMPARE DANS L'ORDRE DES DATES, pas au plus grand compteur connu : une
 * facture saisie en retard porte legitimement un compteur plus bas que le
 * dernier plein. Comparer au maximum ferait crier a tort, et une alerte qui
 * crie a tort, on apprend a l'ignorer -- le mecanisme des 88 faux retards.
 *
 * Incoherent si une operation ANTERIEURE avait deja un compteur plus haut, si
 * une POSTERIEURE en avait un plus bas, ou si l'on passe sous le compteur
 * d'entree en parc. L'operation modifiee ne se compare pas a elle-meme.
 *
 * C'est un AVERTISSEMENT, pas un refus : un compteur remplace recule pour de
 * vrai. Le serveur n'a donc pas de regle ici -- il accepterait a raison. */
export function incoherenceKm(km, date, operations, kmInitial, operationId) {
  if (km === null || km === "" || !date) return null;
  const n = Number(km);
  const lisible = (v) => new Intl.NumberFormat("fr-FR").format(v) + " km";
  const jour = (d) => d.split("-").reverse().join("/");
  if (kmInitial != null && n < Number(kmInitial)) {
    return `Le camion est entré au parc à ${lisible(kmInitial)} : vous saisissez ${lisible(n)}.`;
  }
  const autres = (operations || []).filter(
    (o) => o.kilometrage_releve != null && String(o.id) !== String(operationId)
  );
  const avantPlusHaut = autres
    .filter((o) => o.date_operation < date && Number(o.kilometrage_releve) > n)
    .sort((a, b) => b.kilometrage_releve - a.kilometrage_releve)[0];
  if (avantPlusHaut) {
    return `Le ${jour(avantPlusHaut.date_operation)}, le compteur indiquait déjà `
      + `${lisible(avantPlusHaut.kilometrage_releve)} : vous saisissez ${lisible(n)} au ${jour(date)}.`;
  }
  const apresPlusBas = autres
    .filter((o) => o.date_operation > date && Number(o.kilometrage_releve) < n)
    .sort((a, b) => a.kilometrage_releve - b.kilometrage_releve)[0];
  if (apresPlusBas) {
    return `Le ${jour(apresPlusBas.date_operation)}, plus tard, le compteur n'indiquait que `
      + `${lisible(apresPlusBas.kilometrage_releve)} : vous saisissez ${lisible(n)} au ${jour(date)}.`;
  }
  return null;
}

const OP_VIDE = {
  type_operation: "carburant",
  realise_par: "",
  date_operation: new Date().toISOString().slice(0, 10),
  kilometrage_releve: "",
  cout_ht: "",
  litres: "",
  // Precision obligatoire pour le type « Autre » (H5, 28/09/2026).
  libelle_libre: "",
  notes: "",
};

const txt = (v) => (v === "" || v === null || v === undefined ? null : v);
const nombre = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const nettoyerEntier = (s) => s.replace(/[^0-9]/g, "");
function nettoyerDecimal(s) {
  let v = s.replace(",", ".").replace(/[^0-9.]/g, "");
  const i = v.indexOf(".");
  if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "");
  return v;
}

export default function OperationForm({ user, onLogout }) {
  const { vehiculeId, operationId } = useParams();
  const edition = Boolean(operationId);
  const navigate = useNavigate();

  const modules = useModules();
  // Pieces sorties du magasin par cette intervention. Uniquement a la
  // CREATION : la route de modification ne les prend pas, et la restitution
  // n'existe que sur la suppression. Proposer de les changer ici laisserait
  // croire a un ajustement qui n'aurait pas lieu.
  const [pieces, setPieces] = useState([]);
  // Des pieces sortent-elles du stock ? Alors le cout se calcule (29/09).
  const piecesDuStock = !edition
    && pieces.some((p) => p.article_id && Number(p.quantite) > 0);

  const [form, setForm] = useState(OP_VIDE);
  const [erreurs, setErreurs] = useState({});
  const [erreurGlobale, setErreurGlobale] = useState("");
  const [chargement, setChargement] = useState(edition);
  const [envoi, setEnvoi] = useState(false);
  // L'historique du camion, pour juger le kilometrage saisi (audit B3). UNE
  // PROMESSE, et pas seulement un etat : si l'on valide avant qu'il soit
  // arrive, le controle doit l'ATTENDRE. Avec un simple etat, il jugeait sur
  // un historique vide et laissait tout passer -- trouve par un parcours qui
  // ne tombait que sous charge, quand le chargement etait plus lent.
  const historiqueRef = useRef(null);
  const [alerteKm, setAlerteKm] = useState(null);

  // Lecture automatique d'une facture. Le bouton n'apparait que si le client
  // a demande la fonction : afficher un bouton qui repond 403 est pire que ne
  // pas l'afficher du tout.
  const [lectureDispo, setLectureDispo] = useState(false);
  const [lectureEnCours, setLectureEnCours] = useState(false);
  const [lecture, setLecture] = useState(null); // { remarques, avertissement, cout, champs }
  const fichierRef = useRef(null);

  useEffect(() => {
    api
      .get("/mes-options")
      .then(({ data }) => setLectureDispo(Boolean(data.lecture_factures)))
      .catch(() => setLectureDispo(false));
  }, []);

  const analyserFacture = (fichier) => {
    if (!fichier) return;
    setLectureEnCours(true);
    setLecture(null);
    setErreurGlobale("");

    const donnees = new FormData();
    donnees.append("fichier", fichier);
    api
      .post(`/vehicules/${vehiculeId}/factures/analyser`, donnees)
      .then(({ data }) => {
        // On ne remplit QUE ce qui a ete lu. Ecraser un champ deja saisi par
        // un vide effacerait le travail de l'utilisateur.
        const champs = [];
        setForm((actuel) => {
          const suivant = { ...actuel };
          const poser = (cle, valeur, libelle) => {
            if (valeur === null || valeur === undefined || valeur === "") return;
            suivant[cle] = String(valeur);
            champs.push(libelle);
          };
          poser("type_operation", data.type_operation, "type");
          poser("date_operation", data.date_operation, "date");
          poser("cout_ht", data.cout_ht, "coût HT");
          poser("realise_par", data.fournisseur, "garage");
          poser("kilometrage_releve", data.kilometrage_releve, "kilométrage");
          poser("litres", data.litres, "litres");
          poser("libelle_libre", data.libelle_libre, "libellé");
          return suivant;
        });
        setLecture({
          remarques: data.remarques || [],
          avertissement: data.avertissement,
          cout: data.cout_lecture_eur,
          champs,
          // Le HT est le champ enregistre. Quand seul le TTC a ete lu, le dire
          // plutot que de le convertir : le taux de TVA n'est pas toujours 20 %.
          ttcSeul: data.cout_ht == null && data.cout_ttc != null ? data.cout_ttc : null,
        });
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setErreurGlobale(
          typeof detail === "string"
            ? detail
            : "La lecture a échoué. Saisissez les champs à la main."
        );
      })
      .finally(() => {
        setLectureEnCours(false);
        if (fichierRef.current) fichierRef.current.value = "";
      });
  };

  useEffect(() => {
    // Sans cet historique, l'avertissement ne se declenche simplement pas :
    // un echec de chargement ne doit pas empecher de saisir.
    historiqueRef.current = Promise.all([
      api.get(`/vehicules/${vehiculeId}/operations`),
      api.get(`/vehicules/${vehiculeId}`),
    ])
      .then(([rOps, rVeh]) => ({ operations: rOps.data, kmInitial: rVeh.data.km_initial }))
      .catch(() => ({ operations: [], kmInitial: null }));
  }, [vehiculeId]);

  useEffect(() => {
    if (!edition) return;
    api
      .get(`/vehicules/${vehiculeId}/operations`)
      .then(({ data }) => {
        const op = data.find((o) => String(o.id) === String(operationId));
        if (!op) {
          setErreurGlobale("Opération introuvable.");
          return;
        }
        setForm({
          type_operation: op.type_operation,
          litres: op.litres ?? "",
          realise_par: op.realise_par || "",
          date_operation: op.date_operation || "",
          kilometrage_releve: op.kilometrage_releve ?? "",
          cout_ht: op.cout_ht ?? "",
          libelle_libre: op.libelle_libre || "",
          notes: op.notes || "",
        });
      })
      // Un 404 n'est pas une panne (audit B3, 28/09/2026) : la ressource n'existe
      // pas -- ou appartient a un autre client, que le serveur ne confirme
      // jamais. « Impossible de charger » envoyait chercher un probleme de
      // reseau. Meme message que la fiche vehicule.
      .catch((err) =>
        setErreurGlobale(err?.response?.status === 404
          ? "Opération introuvable."
          : "Impossible de charger l'opération."))
      .finally(() => setChargement(false));
  }, [vehiculeId, operationId, edition]);

  const maj = (champ, valeur) => {
    setForm((f) => ({ ...f, [champ]: valeur }));
    setErreurs((e) => ({ ...e, [champ]: undefined }));
  };

  function valider() {
    const e = {};
    if (!form.date_operation) {
      e.date_operation = "La date est obligatoire.";
    }
    if (form.kilometrage_releve === "" || form.kilometrage_releve === null) {
      e.kilometrage_releve = "Le kilométrage est obligatoire.";
    }
    // Obligatoire SEULEMENT si rien ne sort du stock : les pieces se chiffrent
    // seules (29/09/2026, « ce n'est pas au mecanicien d'enregistrer le
    // prix »). Symetrique de la regle serveur `_cout_si_rien_du_stock`.
    if ((form.cout_ht === "" || form.cout_ht === null) && !piecesDuStock) {
      e.cout_ht = "Le coût HT est obligatoire.";
    }
    if (!form.realise_par.trim()) {
      e.realise_par = "Veuillez indiquer qui a réalisé l'opération.";
    }
    // Symetrique de la regle serveur. Ce n'est PAS elle qui protege -- le
    // serveur refuse de toute facon -- mais elle evite un aller-retour.
    if (form.type_operation === "carburant" && (form.litres === "" || Number(form.litres) <= 0)) {
      e.litres = "Le volume est obligatoire pour un plein.";
    }
    // Symetrique de `schemas.valider_libelle_autre`. Sans ce message, le refus
    // du serveur s'affichait en HAUT de la page, loin du bouton : le
    // mecanicien voyait un clic sans effet et croyait sa saisie perdue.
    if (form.type_operation === "autre" && !form.libelle_libre.trim()) {
      e.libelle_libre = "Précisez de quoi il s'agit.";
    }
    return e;
  }

  const soumettre = async (e) => {
    e.preventDefault();
    setErreurGlobale("");
    const local = valider();
    if (Object.keys(local).length > 0) {
      setErreurs(local);
      return;
    }
    setErreurs({});
    const historique = (await historiqueRef.current) || { operations: [], kmInitial: null };
    const doute = incoherenceKm(form.kilometrage_releve, form.date_operation,
      historique.operations, historique.kmInitial, edition ? operationId : null);
    if (doute) {
      setAlerteKm(doute);
      return;
    }
    await envoyer();
  };

  const envoyer = async () => {
    setEnvoi(true);

    const payload = {
      type_operation: form.type_operation,
      realise_par: form.realise_par.trim(),
      date_operation: form.date_operation,
      kilometrage_releve: nombre(form.kilometrage_releve),
      // Vide avec des pieces du stock : le serveur chiffre seul les pieces.
      cout_ht: form.cout_ht === "" || form.cout_ht === null ? null : Number(form.cout_ht),
      notes: txt(form.notes),
    };
    // Les litres n'accompagnent QUE le carburant : le serveur refuse les deux
    // incoherences (plein sans litres, litres sur autre chose).
    if (form.type_operation === "carburant") {
      payload.litres = nombre(form.litres);
    }
    // LE LIBELLE N'ACCOMPAGNE QUE « AUTRE ». C'etait LE bug H5 : le serveur
    // l'exige pour ce type (422 sans lui), et l'ecran ne l'envoyait jamais --
    // aucun coût « Autre » ne pouvait s'enregistrer, par personne.
    if (form.type_operation === "autre") {
      payload.libelle_libre = form.libelle_libre.trim();
    }
    // Les lignes incompletes ne partent pas : le serveur repondrait 404 sur un
    // article vide, et le message ne dirait pas laquelle des lignes est en
    // cause.
    if (!edition) {
      const utilisees = pieces.filter(
        (p) => p.article_id && Number(p.quantite) > 0
      );
      if (utilisees.length > 0) {
        payload.articles = utilisees.map((p) => ({
          article_id: Number(p.article_id),
          quantite: Number(p.quantite),
        }));
      }
    }

    try {
      if (edition) {
        await api.put(`/operations/${operationId}`, payload);
      } else {
        await api.post(`/vehicules/${vehiculeId}/operations`, payload);
      }
      navigate(`/vehicules/${vehiculeId}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && Array.isArray(detail)) {
        const parChamp = {};
        let global = "";
        for (const item of detail) {
          const parts = (item.loc || []).filter((x) => x !== "body");
          if (parts.length === 0) global = item.msg;
          else parChamp[parts[0]] = item.msg;
        }
        setErreurs(parChamp);
        if (global) setErreurGlobale(global);
      } else {
        setErreurGlobale("Enregistrement impossible. Vérifiez les champs saisis.");
      }
      setEnvoi(false);
    }
  };

  const Err = ({ champ }) =>
    erreurs[champ] ? <span className="erreur-champ">{erreurs[champ]}</span> : null;
  const classeInput = (champ) => (erreurs[champ] ? "champ-invalide" : "");

  if (chargement) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p>Chargement…</p>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <h2>{edition ? "Modifier l'opération" : "Nouvelle opération"}</h2>
        <button className="btn" onClick={() => navigate(`/vehicules/${vehiculeId}`)}>
          Annuler
        </button>
      </div>

      {erreurGlobale && <div className="erreur">{erreurGlobale}</div>}

      {/* Reserve a la creation : sur une modification, les champs sont deja
          renseignes et une relecture les ecraserait sans raison. */}
      {lectureDispo && !edition && (
        <section className="carte carte-lecture">
          <h3>Lire une facture</h3>
          <p className="note-info">
            Photographiez la facture : les champs ci-dessous sont pré-remplis.
            Relisez-les avant d'enregistrer — rien n'est enregistré tout seul.
          </p>
          {/* `image/*` plutot que la liste MIME exacte : avec une liste etroite,
              Safari n'offre pas toujours l'appareil photo. `capture` demande la
              camera arriere. Le serveur valide le vrai type de toute facon --
              on elargit ce que l'utilisateur peut choisir, pas ce qu'on accepte.

              Verifie sur telephone le 30/07/2026 : la camera arriere s'ouvre
              directement, sans passer par le selecteur de fichiers. */}
          <input
            ref={fichierRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => analyserFacture(e.target.files?.[0])}
          />
          <button
            type="button"
            className="btn-accent"
            disabled={lectureEnCours}
            onClick={() => fichierRef.current?.click()}
          >
            {lectureEnCours ? "Lecture en cours…" : "Photographier la facture"}
          </button>

          {lecture && (
            <div className="lecture-resultat">
              {/* L'avertissement d'abord : c'est la seule ligne qui peut
                  annuler la saisie entiere. */}
              {lecture.avertissement && (
                <p className="lecture-alerte">⚠ {lecture.avertissement}</p>
              )}
              <p className="note-recap">
                {lecture.champs.length > 0
                  ? `Rempli : ${lecture.champs.join(", ")}. Vérifiez avant d'enregistrer.`
                  : "Rien n'a pu être lu sur cette image. Saisissez les champs à la main."}
              </p>
              {lecture.ttcSeul != null && (
                <p className="note-info">
                  Seul un montant TTC a été lu ({lecture.ttcSeul} €). Le champ
                  enregistré est le <strong>coût HT</strong> — saisissez-le
                  d'après la facture.
                </p>
              )}
              {lecture.remarques.length > 0 && (
                <ul className="lecture-remarques">
                  {lecture.remarques.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              )}
              {lecture.cout != null && (
                <p className="note-discrete">
                  Coût de cette lecture : {lecture.cout.toFixed(2).replace(".", ",")} €
                </p>
              )}
            </div>
          )}
        </section>
      )}

      <form className="formulaire" onSubmit={soumettre} noValidate>
        <section className="carte">
          <div className="grille-form">
            <label>
              Type d'opération *
              <select
                value={form.type_operation}
                onChange={(e) => maj("type_operation", e.target.value)}
              >
                {TYPES_OPERATION.map((t) => (
                  <option key={t.valeur} value={t.valeur}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            {/* H5 (28/09/2026) — LE CHAMP QUE LA ROUTE EXIGEAIT ET QUE L'ECRAN
                NE PROPOSAIT PAS. Un « Autre » sans precision ne dit rien dans
                l'historique du camion ni dans ses couts : le serveur a raison de
                le refuser. Il manquait seulement le moyen de lui repondre. */}
            {form.type_operation === "autre" && (
              <label>
                Précisez *
                <input
                  className={classeInput("libelle_libre")}
                  placeholder="Ex. : remplacement rétroviseur"
                  value={form.libelle_libre}
                  onChange={(e) => maj("libelle_libre", e.target.value)}
                />
                <Err champ="libelle_libre" />
              </label>
            )}
            <label>
              Réalisé par *
              <input
                className={classeInput("realise_par")}
                placeholder="Mécano, garage…"
                value={form.realise_par}
                onChange={(e) => maj("realise_par", e.target.value)}
              />
              <Err champ="realise_par" />
            </label>
            <label>
              Date *
              <input
                type="date"
                className={classeInput("date_operation")}
                value={form.date_operation}
                onChange={(e) => maj("date_operation", e.target.value)}
              />
              <Err champ="date_operation" />
            </label>
            <label>
              Kilométrage relevé *
              <input
                inputMode="numeric"
                className={classeInput("kilometrage_releve")}
                value={form.kilometrage_releve}
                onChange={(e) => maj("kilometrage_releve", nettoyerEntier(e.target.value))}
              />
              <Err champ="kilometrage_releve" />
            </label>
            {/* LA CASE CHANGE DE ROLE quand des pieces sortent du stock
                (29/09/2026) : elles se chiffrent seules, et ce qu'on tape ici
                s'y AJOUTE (facture de garage, main d'oeuvre facturee). Sans
                piece du stock, c'est le cout de l'operation, obligatoire. */}
            <label>
              {piecesDuStock ? "Autres frais HT (€)" : "Coût HT (€) *"}
              <input
                inputMode="decimal"
                className={classeInput("cout_ht")}
                value={form.cout_ht}
                placeholder={piecesDuStock ? "Garage, main d'œuvre… (facultatif)" : ""}
                onChange={(e) => maj("cout_ht", nettoyerDecimal(e.target.value))}
              />
              {modules.module_stock && !edition && (
                <span className="sous-info">
                  {piecesDuStock
                    ? "Les pièces du stock sont chiffrées automatiquement et s'ajoutent."
                    : "Les pièces sorties du stock (plus bas) se chiffrent toutes seules."}
                </span>
              )}
              <Err champ="cout_ht" />
            </label>
            {/* Le volume n'apparait que pour un plein : afficher un champ sans
                objet sur une vidange encombrerait la saisie la plus frequente. */}
            {form.type_operation === "carburant" && (
              <label>
                Volume (litres) *
                <input
                  inputMode="decimal"
                  className={classeInput("litres")}
                  value={form.litres}
                  onChange={(e) => maj("litres", nettoyerDecimal(e.target.value))}
                />
                <Err champ="litres" />
              </label>
            )}
          </div>
          {form.type_operation === "carburant" && (
            <p className="sous-info">
              Le volume et le kilométrage servent à calculer la consommation.
              Elle apparaît dès le <strong>deuxième plein</strong> : le premier
              remplit le réservoir, il ne mesure aucune distance.
            </p>
          )}
          <label className="champ-large">
            Notes
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => maj("notes", e.target.value)}
            />
          </label>
        </section>

        {/* Le magasin, la ou la question se pose. Sans ce bloc, le mecanicien
            devait ouvrir l'ecran Stock dans un autre onglet pour savoir s'il
            restait des plaquettes -- et la plupart du temps il ne le faisait
            pas. Absent en modification : la route ne reprend pas les pieces. */}
        {modules.module_stock && !edition && (
          <section className="carte">
            <div className="entete-section">
              <h3>Pièces utilisées</h3>
            </div>
            <PiecesConsommees
              vehiculeId={vehiculeId}
              lignes={pieces}
              onChange={setPieces}
            />
          </section>
        )}

        <div className="actions-form">
          <button className="btn-accent" type="submit" disabled={envoi}>
            {envoi ? "Enregistrement…" : edition ? "Enregistrer" : "Ajouter l'opération"}
          </button>
        </div>
      </form>
      {alerteKm && (
        <ModaleConfirmation
          titre="Kilométrage à vérifier"
          message={`${alerteKm} Un compteur ne recule pas : vérifiez le chiffre — ou enregistrez s'il a été remplacé.`}
          libelleConfirmer="Enregistrer quand même"
          libelleAnnuler="Corriger"
          onConfirmer={envoyer}
          onFermer={() => setAlerteKm(null)}
        />
      )}
    </Layout>
  );
}
